import {
  Button,
  ButtonGroup,
  Choice,
  DataStatus,
  Label,
  Separator,
  showNotification,
  Textarea,
  useDialogCloseGuard,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceCaseOrdersQuery,
  useGetServiceItemMoveReasonsQuery,
  usePutServiceOrderItemsToOtherOrderJobV2Mutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {SelectedJobType} from '../../types/SelectedJobType';
import {MoveRequestItemOrder} from '../MoveRequestItemOrder/MoveRequestItemOrder';
import {MoveReasonTemplates} from './components/MoveReasonTemplates';
import {useMoveMechanic} from './hooks/useMoveMechanic';

// Matches the BE limit behind SVC-FE-ER-0239.
const MOVE_REASON_MAX_LENGTH = 1000;

interface MoveRequestItemProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  items: {serviceJobId: string; serviceItemId: string}[];
  hasMaterialItem: boolean;
  hiddenServiceJob?: string | Nullish;
  onMoveItems: () => void;
  onClose: () => void;
}

export function MoveRequestItem(props: MoveRequestItemProps) {
  const [selectedJob, setSelectedJob] = useState<SelectedJobType | Nullish>(null);
  const [moveReason, setMoveReason] = useState<string | undefined>(undefined);

  const [putServiceOrderItemsToOtherOrderJob, {isLoading: isMoveLoading}] =
    usePutServiceOrderItemsToOtherOrderJobV2Mutation();
  // X, Escape and an overlay click must not abandon a running move; Cancel is disabled below.
  useDialogCloseGuard(() => isMoveLoading);
  const {
    data: serviceCaseOrders,
    isLoading,
    isFetching,
    isError,
  } = useGetServiceCaseOrdersQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const {mechanicId, mechanicOptions, areMechanicsLoading, setMechanicId} = useMoveMechanic({
    serviceCaseId: props.serviceCaseId,
    targetServiceOrderId: selectedJob?.orderId,
    isSkipped: not(props.hasMaterialItem),
  });
  const {data: moveReasons} = useGetServiceItemMoveReasonsQuery(undefined, {
    skip: not(props.hasMaterialItem),
  });

  const movingItemIds = props.items.map((item) => item.serviceItemId);

  const defaultMoveReason =
    moveReasons?.find((reason) => reason.isActive && reason.isDefault)?.name ?? '';
  const reason = moveReason ?? defaultMoveReason;

  // BE requires a mechanic and a reason only when a spare part leaves its order.
  const isMoveDetailRequired =
    props.hasMaterialItem && !isNil(selectedJob) && selectedJob.orderId !== props.serviceOrderId;

  const isMoveDetailMissing = isNil(mechanicId) || isNilOrEmpty(reason.trim());

  const handleMoveRequestItem = () => {
    if (isNil(selectedJob)) {
      return;
    }

    putServiceOrderItemsToOtherOrderJob({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        serviceMoveToOrderId: selectedJob.orderId ?? '',
        serviceMoveToJobId: selectedJob.jobId ?? '',
        mechanicId: isMoveDetailRequired ? (mechanicId ?? undefined) : undefined,
        moveReason: isMoveDetailRequired ? reason.trim() : undefined,
        items: props.items,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success();
        props.onMoveItems();
        props.onClose();
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading || isFetching} isError={isError} minHeight={23}>
      <VStack spacing={4}>
        {/* Shown only for a move to another order: within the same order nothing is sent. */}
        <Show when={isMoveDetailRequired}>
          <Choice
            label={i18n.t('entity.orderRequest.labels.selectMechanic')}
            placeholder={i18n.t('general.labels.selectOption')}
            options={mechanicOptions}
            value={mechanicId}
            onChange={(value) => setMechanicId(value)}
            isLoading={areMechanicsLoading}
            isDisabled={isMoveLoading}
            isRequired
            data-testid={suffixTestId('mechanic', props)}
          />
          <VStack spacing={1}>
            <HStack justify="space-between" align="flex-end">
              <Label isRequired>{i18n.t('entity.orderRequest.labels.reasonForMove')}</Label>
              <MoveReasonTemplates
                reason={reason}
                onPrefill={setMoveReason}
                isDisabled={isMoveLoading}
                data-testid={suffixTestId('reasonTemplates', props)}
              />
            </HStack>
            <Textarea
              value={reason}
              onChange={(value) => setMoveReason(value ?? '')}
              placeholder={i18n.t('entity.orderRequest.labels.reasonForMovePlaceholder')}
              maxLength={MOVE_REASON_MAX_LENGTH}
              minRows={1}
              isDisabled={isMoveLoading}
              data-testid={suffixTestId('reason', props)}
            />
          </VStack>
          <Separator spacing={0} />
        </Show>
        <Text color="tertiary" size="small">
          {i18n.t('entity.orderRequest.labels.moveRequestItemDescription')}
        </Text>
        {/* Every order of the service case is listed, closed ones too: the jobs endpoint decides
            through movingItems whether an order can receive the items, and explains why not. */}
        {serviceCaseOrders?.map((order, index) => (
          <Show key={order?.serviceOrderId} when={order}>
            <MoveRequestItemOrder
              serviceCaseId={props.serviceCaseId}
              id={order.serviceOrderId}
              selectedJob={selectedJob}
              onJobSelect={(jobId) => setSelectedJob({jobId, orderId: order.serviceOrderId})}
              hiddenServiceJob={props.hiddenServiceJob}
              movingItemIds={movingItemIds}
              withUncategorizedWithoutItems
              isDisabled={isMoveLoading}
              data-testid={suffixTestId(`order-[${index}]`, props)}
            />
          </Show>
        ))}
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onClick={props.onClose}
            isDisabled={isMoveLoading}
            data-testid={suffixTestId('cancel', props)}
            title={i18n.t('general.actions.cancel')}
          />
          <Button
            onClick={handleMoveRequestItem}
            // isLoading alone blocks only the mouse; Enter or Space would send a second move.
            isDisabled={
              isMoveLoading || isNil(selectedJob) || (isMoveDetailRequired && isMoveDetailMissing)
            }
            isLoading={isMoveLoading}
            data-testid={suffixTestId('move', props)}
            title={i18n.t('entity.order.actions.moveHere')}
          />
        </ButtonGroup>
      </VStack>
    </DataStatus>
  );
}
